import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getBagInvoice, getBags } from '../inventoryApi';
import {
  COLORS, Card, SectionHeader, FilterField, SelectInput,
  PrimaryButton, LinkButton, formatCurrency, MONTH_OPTIONS,
} from './InventoryUIKit';
import InvoiceTable from './InvoiceTable';
import InvoiceSummaryCards from './InvoiceSummaryCards';
import BatchBreakdownModal from './BatchBreakdownModal';

const normalizeItems = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

/**
 * BagInvoice
 *
 * Subtab of "Inventory Invoices": GET /inventory/invoices/bag/?bag_id
 * Shows the FIFO cost of items used out of one technician's bag, with a
 * per-row "View Batch Breakdown" drill-down.
 */
const BagInvoice = () => {
  const now = new Date();
  const [bags, setBags] = useState([]);
  const [technicianFilter, setTechnicianFilter] = useState('');
  const [bagId, setBagId] = useState('');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [breakdownItem, setBreakdownItem] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const bagsData = await getBags();
        setBags(normalizeList(bagsData));
      } catch {
        setBags([]);
      }
    })();
  }, []);

  const technicianOptions = useMemo(() => {
    const map = new Map();
    bags.forEach((b) => {
      const tech = b.current_technician_details;
      if (tech?.id) map.set(tech.id, tech.name || `Technician ${tech.id}`);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [bags]);

  const bagOptions = useMemo(() => {
    return bags.filter((b) => !technicianFilter || String(b.current_technician_details?.id) === String(technicianFilter));
  }, [bags, technicianFilter]);

  useEffect(() => {
    if (bagOptions.length > 0 && !bagOptions.some((b) => String(b.id) === String(bagId))) {
      setBagId(bagOptions[0].id);
    }
    if (bagOptions.length === 0) setBagId('');
  }, [bagOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvoice = useCallback(async () => {
    if (!bagId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getBagInvoice({ bag_id: bagId, month, year });
      setData(result);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load bag invoice.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bagId]);

  useEffect(() => { if (bagId) fetchInvoice(); }, [bagId, fetchInvoice]);

  const selectedBag = bags.find((b) => String(b.id) === String(bagId));
  const items = normalizeItems(data);
  const totalCost = data?.total_cost != null ? Number(data.total_cost) : items.reduce((acc, i) => acc + Number(i.fifo_cost ?? 0), 0);
  const itemsUsed = Number(data?.total_quantity_used || 0);
  const technicianName =
    data?.technician_name ||
    selectedBag?.current_technician_details?.name ||
    '—';
  const assignedMonth = data?.assigned_month || (selectedBag ? `${selectedBag.month}/${selectedBag.year}` : `${month}/${year}`);
  const columns = [
  {
    key: "inventory_item_name",
    label: "Inventory Item",
  },
  {
    key: "quantity_used",
    label: "Quantity Used",
    align: "right",
  },
  {
    key: "total_cost",
    label: "Cost",
    align: "right",
    render: (r) => formatCurrency(Number(r.total_cost)),
  },
  {
    key: "breakdown",
    label: "Batch Breakdown",
    align: "center",
    render: (r) => (
      <LinkButton onClick={() => setBreakdownItem(r)}>
        View Batch Breakdown
      </LinkButton>
    ),
  },
];

  const footer = (
    <tr style={{ borderTop: `2px solid ${COLORS.mediumGrey}` }}>
      <td colSpan={2} style={{ padding: '10px 12px', fontWeight: '700', textAlign: 'right', color: COLORS.darkText }}>Grand Total</td>
      <td style={{ padding: '10px 12px', fontWeight: '700', textAlign: 'right', color: COLORS.darkText }}>{formatCurrency(totalCost)}</td>
      <td />
    </tr>
  );

const breakdownRows = useMemo(() => {
  if (!breakdownItem) return [];

  const batches = breakdownItem.batch_breakdown || [];

  return batches.map((b, idx) => ({
    key: b.id ?? idx,
    purchase_batch: b.purchase_batch ?? `Batch #${idx + 1}`,
    purchase_date: b.purchase_date,
    supplier: b.supplier,
    invoice_number: b.invoice_number || "—",
    quantity_taken: b.quantity_used ?? 0,
    unit_price: Number(b.unit_price),
    total_cost: Number(b.total_price),
  }));
}, [breakdownItem]);

  const breakdownColumns = [
    { key: 'purchase_batch', label: 'Purchase Batch' },
    { key: 'purchase_date', label: 'Purchase Date', render: (r) => r.purchase_date ? new Date(r.purchase_date).toLocaleDateString('en-IN') : '—' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'invoice_number', label: 'Invoice Number' },
    { key: 'quantity_taken', label: 'Quantity Taken', align: 'right' },
    { key: 'unit_price', label: 'Unit Price', align: 'right', render: (r) => formatCurrency(r.unit_price) },
    { key: 'total_cost', label: 'Total Cost', align: 'right', render: (r) => formatCurrency(r.total_cost) },
  ];

  return (
    <div>
      <Card style={{ padding: '16px 20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FilterField label="Technician" minWidth="170px">
            <SelectInput value={technicianFilter} onChange={(e) => setTechnicianFilter(e.target.value)}>
              <option value="">All technicians</option>
              {technicianOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </SelectInput>
          </FilterField>
          <FilterField label="Bag" minWidth="160px">
            <SelectInput value={bagId} onChange={(e) => setBagId(e.target.value)}>
              {bagOptions.length === 0 && <option value="">No bags found</option>}
              {bagOptions.map((b) => <option key={b.id} value={b.id}>BAG-{b.id} ({b.current_technician_details?.name || 'Unassigned'})</option>)}
            </SelectInput>
          </FilterField>
          <FilterField label="Month" minWidth="140px">
            <SelectInput value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </SelectInput>
          </FilterField>
          <FilterField label="Year" minWidth="110px">
            <SelectInput value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[year, year - 1, year - 2, year - 3].map((y) => <option key={y} value={y}>{y}</option>)}
            </SelectInput>
          </FilterField>
          <div style={{ marginLeft: 'auto' }}>
            <PrimaryButton onClick={fetchInvoice} disabled={loading || !bagId}>
              {loading ? 'Generating…' : '📄 Generate'}
            </PrimaryButton>
          </div>
        </div>
      </Card>

      <InvoiceSummaryCards
        cards={[
          { title: 'Bag ID', value: bagId ? `BAG-${bagId}` : '—', icon: '🎒', accentColor: '#6366f1', loading, error },
          { title: 'Technician', value: technicianName, icon: '👤', accentColor: COLORS.vividPurple, loading, error },
          { title: 'Assigned Month', value: assignedMonth, icon: '🗓️', accentColor: COLORS.grassGreen, loading, error },
          { title: 'Total Cost', value: formatCurrency(totalCost), icon: '💰', accentColor: COLORS.aquaBlue, loading, error },
          { title: 'Items Used', value: itemsUsed, icon: '📦', accentColor: COLORS.aquaBlue, loading, error },
        ]}
      />

      <Card>
        <SectionHeader title="Bag Invoice" subtitle={bagId ? `BAG-${bagId} · ${technicianName}` : undefined} accentColor={COLORS.aquaBlue} />
        <div style={{ padding: '16px' }}>
          <InvoiceTable
            columns={columns}
            rows={items}
            loading={loading}
            error={error}
            onRetry={fetchInvoice}
            emptyMessage="No inventory usage recorded for this bag in the selected month."
            footer={items.length > 0 ? footer : null}
            rowKey="id"
          />
        </div>
      </Card>

      {breakdownItem && (
        <BatchBreakdownModal
          title="Batch Breakdown"
          subtitle={breakdownItem.inventory_item_name || breakdownItem.inventory_item?.item_name || breakdownItem.item_name}
          columns={breakdownColumns}
          rows={breakdownRows}
          grandTotal={breakdownRows.reduce((acc, r) => acc + Number(r.total_cost || 0), 0)}
          onClose={() => setBreakdownItem(null)}
        />
      )}
    </div>
  );
};

export default BagInvoice;
